/**
 * Typed client for the staff console directory (`GET /api/staff/organizations`).
 *
 * Cross-tenant and NOT workspace-scoped, so it uses the plain authed
 * `apiRequest` (Bearer JWT) rather than the workspace-bound fetcher. Access is
 * enforced server-side by `PlatformStaffGuard`; the dashboard only shows the
 * surface to `platform:staff` as a UX nicety.
 */

import { apiRequest } from "../../agents/api/client";

export interface StaffWorkspace {
  id: string;
  name: string;
  slug: string;
  environment: string;
  onboardingState: string;
  memberCount: number;
  createdAt: string;
}

export interface StaffOrganization {
  id: string;
  name: string;
  slug: string;
  createdAt: string;
  workspaces: StaffWorkspace[];
}

export interface StaffOrgPage {
  items: StaffOrganization[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface ListStaffOrgsParams {
  search?: string;
  cursor?: string;
  limit?: number;
}

interface StaffWorkspaceDto {
  id: string;
  name: string;
  slug: string;
  environment: string;
  onboarding_state: string;
  member_count: number;
  created_at: string;
}

interface StaffOrganizationDto {
  id: string;
  name: string;
  slug: string;
  created_at: string;
  workspaces: StaffWorkspaceDto[];
}

interface StaffOrgPageDto {
  items: StaffOrganizationDto[];
  next_cursor: string | null;
  has_more: boolean;
}

function mapWorkspace(dto: StaffWorkspaceDto): StaffWorkspace {
  return {
    id: dto.id,
    name: dto.name,
    slug: dto.slug,
    environment: dto.environment,
    onboardingState: dto.onboarding_state,
    memberCount: dto.member_count,
    createdAt: dto.created_at,
  };
}

function mapOrganization(dto: StaffOrganizationDto): StaffOrganization {
  return {
    id: dto.id,
    name: dto.name,
    slug: dto.slug,
    createdAt: dto.created_at,
    workspaces: dto.workspaces.map(mapWorkspace),
  };
}

export async function listStaffOrganizations(
  params: ListStaffOrgsParams = {},
): Promise<StaffOrgPage> {
  const dto = await apiRequest<StaffOrgPageDto>("GET", "/api/staff/organizations", undefined, {
    search: params.search,
    cursor: params.cursor,
    limit: params.limit,
  });
  return {
    items: dto.items.map(mapOrganization),
    nextCursor: dto.next_cursor,
    hasMore: dto.has_more,
  };
}
